import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getStockActivityLogs } from "../../redux/Inventory/inventoryActions";
import EmptyState from "../../components/EmptyState";
import moment from "moment";
import { ContentCopy } from "@mui/icons-material";
import toast from "react-hot-toast";
import {
  LocalShipping,
  AssignmentTurnedIn,
  Cancel,
  TwoWheeler,
  Inventory,
} from "@mui/icons-material";

const getActivityTypeBadgeStyle = (activityType) => {
  const styles = {
    STOCK_IN: "border-[#2B8C34] text-[#2B8C34]",
    STOCK_OUT: "border-[#B92020] text-[#B92020] ",
    STOCK_TRANSFER: "border-[#0066CC] text-[#0066CC] ",
    "STOCK_TRANSFER-OUTGOING": "border-[#0066CC] text-[#0066CC] ",
    "STOCK_TRANSFER-INCOMING": "border-[#0066CC] text-[#0066CC] ",
    STOCK_ADJUSTMENT: "border-[#6B46C1] text-[#6B46C1] ",
    OUT_OF_STOCK: "border-[#B92020] text-[#B92020]",
    LOW_STOCK: "border-[#D4A407] text-[#D4A407] ",
    BACK_IN_STOCK: "border-[#2B8C34] text-[#2B8C34] ",
    NEW_PRODUCT: "border-[#6B46C1] text-[#6B46C1] ",
    NEW_VARIANT: "border-[#6B46C1] text-[#6B46C1] ",
    STOCK_REQUEST: "border-[#D4A407] text-[#D4A407] ",
    STOCK_CONFIRMATION: "border-[#2B8C34] text-[#2B8C34] ",
    PURCHASE: "border-[#D4A407] text-[#D4A407]",
  };
  return styles[activityType] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
};

const getStatusBadgeStyle = (status) => {
  const styles = {
    DISPATCHED: "border-[#0066CC] text-[#0066CC] ",
    DELIVERED: "border-[#2B8C34] text-[#2B8C34] ",
    PROCESSING: "border-[#D4A407] text-[#D4A407] ",
    COMPLETED: "border-[#2B8C34] text-[#2B8C34] ",
    CANCELLED: "border-[#B92020] text-[#B92020] ",
    PENDING: "border-[#D4A407] text-[#D4A407] ",
  };
  return styles[status] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
};

const getStatusIcon = (status) => {
  const iconProps = { style: { fontSize: 14 } };
  switch (status?.toUpperCase()) {
    case "DISPATCHED":
      return <LocalShipping {...iconProps} />;
    case "DELIVERED":
    case "COMPLETED":
      return <AssignmentTurnedIn {...iconProps} />;
    case "CANCELLED":
      return <Cancel {...iconProps} />;
    case "PROCESSING":
      return <Inventory {...iconProps} />;

    default:
      return null;
  }
};

const handleCopyNumber = (number) => {
  navigator.clipboard.writeText(number);
  toast.success(`${number} copied successfully`);
};

const capitalizeFirstLetter = (str) => {
  return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase() || "";
};

const ActivityLog = ({ stockId, maxHeight = "100%" }) => {
  const dispatch = useDispatch();
  const {
    stockActivityLogs,
    stockActivityLogsLoading,
    stockActivityLogsError,
  } = useSelector((state) => state.inventory);

  useEffect(() => {
    if (stockId) {
      dispatch(getStockActivityLogs({ stockId }));
    }
  }, [dispatch, stockId]);

  if (stockActivityLogsLoading) {
    return (
      <div className="bg-white rounded-lg p-4 flex-1 border-t border-[#E1E6E1] ">
        <div className="flex border-b border-[#E1E6E1] justify-between items-center px-6 pb-4 mb-4">
          <p className="text-sm text-[#2E332F] font-semibold">Activity Log</p>
        </div>
        <div className="animate-pulse">
          <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
          <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
          <div className="h-12 bg-gray-200 rounded-lg w-full mb-4"></div>
        </div>
      </div>
    );
  }

  if (stockActivityLogsError) {
    return (
      <div className="bg-white border-t border-[#E1E6E1] rounded-lg p-4 flex-1">
        <div className="flex border-b border-[#E1E6E1] justify-between items-center px-6 pb-4 mb-4">
          <p className="text-sm text-[#2E332F] font-semibold">Activity Log</p>
        </div>
        <div className="text-red-500 text-center py-8">
          Error loading activity logs: {stockActivityLogsError}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white  flex-1 flex flex-col border-t border-[#E1E6E1] ">
      <div className="flex border-b border-[#E1E6E1] justify-between items-center px-6 py-4">
        <p className="text-sm text-[#2E332F] font-semibold">Activity Log</p>
      </div>

      <div className="overflow-y-auto flex-1" style={{ maxHeight }}>
        {!stockActivityLogs || stockActivityLogs.length === 0 ? (
          <div className="py-8">
            <EmptyState text="No activity logs found for this stock" />
          </div>
        ) : (
          stockActivityLogs.map((activity, index) => (
            <div
              key={activity.id}
              className={` ${
                index < stockActivityLogs.length - 1
                  ? "border-b border-[#E1E6E1]"
                  : ""
              }`}
            >
              {/* Activity Type Badge and Timestamp */}
              <div className="flex justify-between items-center bg-[#F6F7F6] px-6 py-2">
                <span
                  className={`px-2 py-1 rounded border ${getActivityTypeBadgeStyle(
                    activity.activity_type
                  )} font-semibold text-xs`}
                >
                  {activity.activity_type_display || activity.activity_type}
                </span>
                <span className="text-xs text-[#5F6D60] font-semibold">
                  {moment(activity.timestamp).format("h:mm A")}{" "}
                  <span className="text-[#A1A8A1] mr-2">|</span>
                  {moment(activity.timestamp).format("DD MMM YY")}
                </span>
              </div>

              {/* Activity Details */}
              <div className="space-y-0">
                {/* Unit Added/Transferred */}
                {activity.details?.quantity && (
                  <div className="flex items-center border-t border-[#E1E6E1] py-2 px-6">
                    <span className="text-xs text-[#5F6D60] mr-2">
                      {activity.activity_type === "STOCK_IN"
                        ? "Unit Added:"
                        : activity.activity_type.includes("TRANSFER")
                        ? "Unit Transferred:"
                        : "Unit Bought:"}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2 py-1 rounded border ${
                        activity.activity_type === "STOCK_IN" ||
                        activity.activity_type === "STOCK_TRANSFER-INCOMING"
                          ? "text-[#2B8C34] border-[#2B8C34] bg-[#EDF7EE]"
                          : "text-[#B92020] border-[#B92020] bg-[#FEE2E2]"
                      }`}
                    >
                      {activity.activity_type === "STOCK_IN" ||
                      activity.activity_type === "STOCK_TRANSFER-INCOMING"
                        ? "+"
                        : "-"}
                      {activity.details.quantity}
                    </span>
                  </div>
                )}

                {/* Stock Added By / Transferred By */}
                {activity.actor && (
                  <div className="flex items-center border-t border-[#E1E6E1] py-2 px-6">
                    <span className="text-xs text-[#5F6D60] mr-2">
                      {activity.activity_type === "STOCK_IN"
                        ? "Stock Added By:"
                        : activity.activity_type.includes("TRANSFER")
                        ? "Stock Transferred By:"
                        : "Sold By:"}
                    </span>
                    <span className="text-xs font-semibold text-[#2E332F]">
                      {activity.actor.name || activity.actor.username}
                    </span>
                    {activity.actor.role && (
                      <span className="text-xs text-[#5F6D60] ml-2 bg-[#F6F7F6] font-semibold border border-[#E1E6E1] px-2 py-1 rounded">
                        {activity.actor.role}
                      </span>
                    )}
                  </div>
                )}

                {/* Source Store for TRANSFER-INCOMING */}
                {activity.activity_type === "STOCK_TRANSFER-INCOMING" &&
                  activity.details?.source_store && (
                    <div className="flex items-center border-t  border-[#E1E6E1] py-2 px-6">
                      <span className="text-xs text-[#5F6D60] mr-2">
                        Source Store:
                      </span>
                      <span className="text-xs text-[#5F6D60] font-semibold bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded">
                        {activity.details.source_store}
                      </span>
                    </div>
                  )}

                {/* Receiving Store for TRANSFER-OUTGOING */}
                {activity.activity_type === "STOCK_TRANSFER-OUTGOING" &&
                  activity.details?.receiving_store && (
                    <div className="flex items-center border-t border-[#E1E6E1] py-2 px-6">
                      <span className="text-xs text-[#5F6D60] mr-2">
                        Receiving Store:
                      </span>
                      <span className="text-xs text-[#5F6D60] font-semibold bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded">
                        {activity.details.receiving_store}
                      </span>
                    </div>
                  )}

                {/* Location/Store Information for other activity types */}
                {!activity.activity_type.includes("TRANSFER") &&
                  activity.details?.location && (
                    <div className="items-center border-t border-[#E1E6E1] flex justify-between pr-2 py-2 pl-6">
                      <div className="flex items-center">
                        <span className="text-xs text-[#5F6D60] mr-2">
                          {activity.activity_type === "PURCHASE"
                            ? "Channel:"
                            : "Location:"}
                        </span>

                        <span
                          className={`text-xs font-semibold ${
                            activity.activity_type === "PURCHASE"
                              ? "text-[#5F6D60] bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded"
                              : "text-[#2E332F]"
                          }`}
                        >
                          {activity.details.location}
                        </span>
                      </div>

                      {activity.activity_type === "PURCHASE" ? (
                        <span className="text-xs text-[#5F6D60] font-semibold bg-[#F6F7F6] border border-[#E1E6E1] px-2 py-1 rounded flex items-center gap-1 ml-2">
                          <TwoWheeler
                            className="text-[#5F6D60] cursor-pointer"
                            style={{ fontSize: "12px" }}
                          />
                          {activity?.deliveryType}
                        </span>
                      ) : null}
                    </div>
                  )}

                {/* Order ID for purchases */}
                {activity.details?.reference &&
                  activity.activity_type === "PURCHASE" && (
                    <div className="flex items-center border-t  border-[#E1E6E1] py-2 px-6">
                      <span className="text-xs text-[#5F6D60] mr-2">
                        Order ID:
                      </span>
                      <div className="border border-[#E1E6E1] px-1 py-1 rounded bg-white w-fit gap-0.5 flex items-center text-xs text-[#5F6D60] font-semibold">
                        {activity.details.reference}
                        <ContentCopy
                          className="text-[#5F6D60] cursor-pointer"
                          style={{ fontSize: "10px" }}
                          onClick={() =>
                            handleCopyNumber(activity.details.reference)
                          }
                        />
                      </div>
                    </div>
                  )}

                {/* Status with Icon */}
                {activity.details?.status && (
                  <div className="flex items-center border-t border-[#E1E6E1] py-2 px-6">
                    <span className="text-xs text-[#5F6D60] mr-2">Status:</span>
                    <div className="flex items-center">
                      <span
                        className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                          activity.details.status
                        )} font-semibold text-xs flex items-center gap-1`}
                      >
                        {getStatusIcon(activity.details.status)}
                        {capitalizeFirstLetter(activity.details.status)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ActivityLog;
